import React from "react";
import {
  TableBody,
  TableRow,
  TableCell,
  Box,
  Skeleton,
  SxProps,
  Theme,
  Card,
  CardActionArea,
} from "@mui/material";
import { exerciseLibraryStyles } from "../exercise-library/exerciseLibraryStyles";
import clsx from "clsx";
import { makeStyles } from "@mui/styles";
import { getColumnWidths } from "../exercise-library/ExercisesTableRow";

interface SkeletonLoaderProps {
  width?: string | number;
  height?: number;
  variant?: "text" | "circular" | "rectangular";
  sx?: SxProps<Theme>;
}
const useStyles = makeStyles((theme) => ({
  actionArea: {
    padding: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
    gap: theme.spacing(2),
    justifyContent: "start",
  },

  textContainer: {
    width: "60%",
    display: "flex",
    flexDirection: "column",
    alignItems: "start",
  },
}));

export const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({
  width = "60%",
  height = 20,
  variant = "text",
  sx = {},
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Skeleton variant={variant} width={width} height={height} sx={sx} />
    </Box>
  );
};

interface ExerciseLibraryTableSkeleton {
  loading?: boolean;
  className?: string;
  isPendingTab?: boolean;
}

export const ExerciseLibraryTableSkeleton: React.FC<
  ExerciseLibraryTableSkeleton
> = ({ className, isPendingTab }) => {
  const columnWidth = getColumnWidths(isPendingTab);
  return (
    <TableBody className={className}>
      <TableRow hover>
        <TableCell
          component="th"
          scope="row"
          sx={{ width: columnWidth.thumbnail }}
        >
          <SkeletonLoader
            variant="circular"
            width={58}
            height={58}
            sx={{ borderRadius: 2, justifyContent: "flex-start" }}
          />
        </TableCell>

        <TableCell component="th" scope="row" sx={{ width: columnWidth.title }}>
          <Box sx={{ width: "80%" }}>
            <Skeleton width={"80%"} />
            <Skeleton width={"100%"} />
          </Box>
        </TableCell>

        {!isPendingTab ? (
          <>
            <TableCell
              align="center"
              sx={{ width: columnWidth.movementPattern }}
            >
              <SkeletonLoader width="60%" />
            </TableCell>

            <TableCell align="center" sx={{ width: columnWidth.bodypart }}>
              <SkeletonLoader width="60%" />
            </TableCell>

            <TableCell align="center" sx={{ width: columnWidth.tags }}>
              <SkeletonLoader width="60%" />
            </TableCell>
          </>
        ) : (
          <TableCell align="left" sx={{ width: columnWidth.suggestedBy }}>
            <SkeletonLoader width="60%" />
          </TableCell>
        )}

        {!isPendingTab && (
          <TableCell align="center" sx={{ width: columnWidth.images }}>
            <SkeletonLoader width="60%" />
          </TableCell>
        )}

        <TableCell align="center" sx={{ width: columnWidth.actions }}>
          <SkeletonLoader width="60%" />
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface ExerciseLibraryCardSkeletonProps {
  className?: string;
}

export const ExerciseLibraryCardSkeleton: React.FC<
  ExerciseLibraryCardSkeletonProps
> = ({ className }) => {
  const s = exerciseLibraryStyles();
  const styles = useStyles();
  return (
    <Card className={clsx(s.mobileCard, className)}>
      <CardActionArea className={styles.actionArea}>
        <SkeletonLoader
          variant="circular"
          width={58}
          height={58}
          sx={{ borderRadius: 2 }}
        />

        <Box className={styles.textContainer}>
          <Skeleton width="80%" height={22} sx={{ mb: 1 }} />
          <Skeleton width="100%" height={18} />
          <Skeleton width="60%" height={18} sx={{ mt: 1 }} />
        </Box>
      </CardActionArea>
    </Card>
  );
};
